package app.footyguide.web.data

import app.footyguide.core.theme.ClubCatalog
import app.footyguide.domain.model.BroadcastStatus
import app.footyguide.domain.model.Game
import app.footyguide.domain.model.MatchStatus

enum class StandingZone {
    LIBERTADORES,
    PRE_LIBERTADORES,
    SUL_AMERICANA,
    NEUTRAL,
    RELEGATION
}

data class StandingRow(
    val position: Int,
    val teamId: String,
    val name: String,
    val initials: String,
    val logoUrl: String,
    val points: Int,
    val played: Int,
    val wins: Int,
    val draws: Int,
    val losses: Int,
    val goalDifference: Int,
    val zone: StandingZone
)

data class TeamHubData(
    val teamId: String,
    val nextGame: Game,
    val recentMatches: List<Game>,
    val upcomingMatches: List<Game>,
    val quizTitle: String,
    val quizDescription: String,
    val quizUrl: String,
    val hasQuiz: Boolean = true
)

private data class MatchFixture(
    val opponent: String,
    val isHome: Boolean,
    val matchTime: String,
    val stadiumName: String,
    val stadiumCityName: String
)

private data class MatchAnchorData(
    val opponent: String,
    val isHome: Boolean,
    val matchTime: String,
    val stadiumName: String,
    val stadiumCityName: String
)

object MockDataProvider {

    /**
     * Tabela realista da Série A do Brasileirão (20 clubes).
     */
    fun getStandings(): List<StandingRow> {
        val clubs = listOf(
            Triple("flamengo", 73, 40),
            Triple("palmeiras", 70, 34),
            Triple("cruzeiro", 57, 6),
            Triple("fluminense", 54, 10),
            Triple("athleticopr", 54, 10),
            Triple("bahia", 53, 6),
            Triple("atleticomg", 50, 5),
            Triple("bragantino", 47, 5),
            Triple("santos", 47, 1),
            Triple("saopaulo", 43, 0),
            Triple("coritiba", 42, -10),
            Triple("botafogo", 39, -8),
            Triple("vasco", 38, -9),
            Triple("vitoria", 38, -13),
            Triple("gremio", 37, -7),
            Triple("mirassol", 37, -10),
            Triple("corinthians", 36, -6),
            Triple("internacional", 35, -5),
            Triple("remo", 29, -15),
            Triple("chapecoense", 21, -34)
        )

        return clubs.mapIndexed { index, (slug, pts, gd) ->
            val pos = index + 1
            val club = ClubCatalog.bySlug(slug)
            val name = club?.name ?: slug.replaceFirstChar { it.uppercase() }
            val initials = club?.initials ?: slug.take(3).uppercase()
            val logo = club?.logoUrl ?: "https://storage.googleapis.com/footy-guide.firebasestorage.app/public/logos/teams/$slug.webp"
            
            val zone = when {
                pos in 1..4 -> StandingZone.LIBERTADORES
                pos in 5..6 -> StandingZone.PRE_LIBERTADORES
                pos in 7..12 -> StandingZone.SUL_AMERICANA
                pos in 17..20 -> StandingZone.RELEGATION
                else -> StandingZone.NEUTRAL
            }

            val played = when (slug) {
                "bragantino", "santos" -> 34
                "vasco", "chapecoense" -> 32
                else -> 33
            }
            val wins = when (slug) {
                "flamengo" -> 22
                "palmeiras" -> 20
                "cruzeiro" -> 17
                "fluminense", "athleticopr" -> 15
                "bahia", "atleticomg" -> 14
                "bragantino" -> 13
                "santos", "saopaulo" -> 12
                "coritiba" -> 11
                "botafogo", "vasco", "vitoria" -> 10
                "gremio", "mirassol", "corinthians" -> 9
                "internacional" -> 8
                "remo" -> 6
                "chapecoense" -> 4
                else -> 10
            }
            val draws = pts - (wins * 3)
            val losses = played - wins - draws

            StandingRow(
                position = pos,
                teamId = slug,
                name = name,
                initials = initials,
                logoUrl = logo,
                points = pts,
                played = played,
                wins = wins,
                draws = maxOf(0, draws),
                losses = maxOf(0, losses),
                goalDifference = gd,
                zone = zone
            )
        }
    }

    /**
     * Hub do Torcedor com próximo jogo, histórico recente, calendário e quiz Alefly.
     */
    fun getTeamHubData(teamId: String, baseDate: String = "2026-11-03"): TeamHubData {
        val club = ClubCatalog.bySlug(teamId)
        val clubName = club?.name ?: teamId.replaceFirstChar { it.uppercase() }
        val nickname = club?.nickname ?: clubName

        val (opponent, isHome, matchTime, stadiumName, stadiumCityName) = when (teamId) {
            "botafogo" -> MatchFixture("atleticomg", true, "2026-11-04T21:30:00-03:00", "Nilton Santos", "Rio de Janeiro")
            "flamengo" -> MatchFixture("gremio", true, "2026-11-04T19:30:00-03:00", "Maracanã", "Rio de Janeiro")
            "gremio" -> MatchFixture("flamengo", false, "2026-11-04T19:30:00-03:00", "Maracanã", "Rio de Janeiro")
            "atleticomg" -> MatchFixture("botafogo", false, "2026-11-04T21:30:00-03:00", "Nilton Santos", "Rio de Janeiro")
            "palmeiras" -> MatchFixture("remo", true, "2026-11-05T19:30:00-03:00", "Allianz Parque", "São Paulo")
            "remo" -> MatchFixture("palmeiras", false, "2026-11-05T19:30:00-03:00", "Allianz Parque", "São Paulo")
            "saopaulo" -> MatchFixture("corinthians", true, "2026-11-04T21:30:00-03:00", "MorumBIS", "São Paulo")
            "corinthians" -> MatchFixture("saopaulo", false, "2026-11-04T21:30:00-03:00", "MorumBIS", "São Paulo")
            "athleticopr" -> MatchFixture("vasco", true, "2026-11-04T19:30:00-03:00", "Ligga Arena", "Curitiba")
            "vasco" -> MatchFixture("athleticopr", false, "2026-11-04T19:30:00-03:00", "Ligga Arena", "Curitiba")
            "internacional" -> MatchFixture("coritiba", true, "2026-11-03T21:30:00-03:00", "Beira-Rio", "Porto Alegre")
            "coritiba" -> MatchFixture("internacional", false, "2026-11-03T21:30:00-03:00", "Beira-Rio", "Porto Alegre")
            "chapecoense" -> MatchFixture("mirassol", true, "2026-11-03T19:30:00-03:00", "Arena Condá", "Chapecó")
            "mirassol" -> MatchFixture("chapecoense", false, "2026-11-03T19:30:00-03:00", "Arena Condá", "Chapecó")
            "cruzeiro" -> MatchFixture("bahia", true, "2026-11-05T21:30:00-03:00", "Mineirão", "Belo Horizonte")
            "bahia" -> MatchFixture("cruzeiro", false, "2026-11-05T21:30:00-03:00", "Mineirão", "Belo Horizonte")
            "vitoria" -> MatchFixture("fluminense", true, "2026-11-06T20:00:00-03:00", "Barradão", "Salvador")
            "fluminense" -> MatchFixture("vitoria", false, "2026-11-06T20:00:00-03:00", "Barradão", "Salvador")
            "bragantino" -> MatchFixture("santos", true, "2026-11-02T20:00:00-03:00", "Nabi Abi Chedid", "Bragança Paulista")
            "santos" -> MatchFixture("bragantino", false, "2026-11-02T20:00:00-03:00", "Nabi Abi Chedid", "Bragança Paulista")
            else -> MatchFixture("palmeiras", true, "${baseDate}T16:00:00-03:00", "Estádio Principal", "Brasil")
        }

        val homeTeam = if (isHome) teamId else opponent
        val awayTeam = if (isHome) opponent else teamId

        val nextGame = Game(
            id = "${homeTeam}_vs_${awayTeam}",
            competitionId = "brasileirao",
            matchday = 33,
            homeTeamId = homeTeam,
            awayTeamId = awayTeam,
            teamIds = listOf(homeTeam, awayTeam),
            kickoff = matchTime,
            date = matchTime.substringBefore("T"),
            channelsByCountry = mapOf(
                "BR" to when {
                    homeTeam == "athleticopr" -> listOf("cazetv", "tnt")
                    homeTeam == "botafogo" || homeTeam == "saopaulo" -> listOf("globo", "premiere")
                    else -> listOf("sportv", "premiere")
                }
            ),
            broadcastStatusByCountry = mapOf("BR" to BroadcastStatus.CONFIRMED),
            matchStatus = if (matchTime.startsWith("2026-11-02")) MatchStatus.FINISHED else MatchStatus.NOT_STARTED,
            homeScore = if (matchTime.startsWith("2026-11-02")) 2 else null,
            awayScore = if (matchTime.startsWith("2026-11-02")) 1 else null,
            stadium = stadiumName,
            stadiumCity = stadiumCityName,
        )

        val recentMatches = listOf(
            Game(
                id = "${teamId}_vs_corinthians",
                competitionId = "copa-do-brasil",
                homeTeamId = teamId,
                awayTeamId = "corinthians",
                teamIds = listOf(teamId, "corinthians"),
                kickoff = "2026-10-28T21:30:00Z",
                date = "2026-10-28",
                channelsByCountry = mapOf("BR" to listOf("globo", "sportv", "premiere")),
                broadcastStatusByCountry = mapOf("BR" to BroadcastStatus.CONFIRMED),
                matchStatus = MatchStatus.FINISHED,
                homeScore = 2,
                awayScore = 0
            ),
            Game(
                id = "saopaulo_vs_$teamId",
                competitionId = "brasileirao",
                homeTeamId = "saopaulo",
                awayTeamId = teamId,
                teamIds = listOf("saopaulo", teamId),
                kickoff = "2026-10-24T19:00:00Z",
                date = "2026-10-24",
                channelsByCountry = mapOf("BR" to listOf("premiere")),
                broadcastStatusByCountry = mapOf("BR" to BroadcastStatus.CONFIRMED),
                matchStatus = MatchStatus.FINISHED,
                homeScore = 1,
                awayScore = 2
            ),
            Game(
                id = "${teamId}_vs_botafogo",
                competitionId = "brasileirao",
                homeTeamId = teamId,
                awayTeamId = "botafogo",
                teamIds = listOf(teamId, "botafogo"),
                kickoff = "2026-10-20T16:00:00Z",
                date = "2026-10-20",
                channelsByCountry = mapOf("BR" to listOf("globo", "premiere")),
                broadcastStatusByCountry = mapOf("BR" to BroadcastStatus.CONFIRMED),
                matchStatus = MatchStatus.FINISHED,
                homeScore = 1,
                awayScore = 1
            )
        )

        val upcomingMatches = listOf(
            nextGame,
            Game(
                id = "vasco_vs_$teamId",
                competitionId = "brasileirao",
                homeTeamId = "vasco",
                awayTeamId = teamId,
                teamIds = listOf("vasco", teamId),
                kickoff = "2026-11-08T18:30:00Z",
                date = "2026-11-08",
                channelsByCountry = mapOf("BR" to listOf("premiere", "cazetv")),
                broadcastStatusByCountry = mapOf("BR" to BroadcastStatus.CONFIRMED),
                matchStatus = MatchStatus.NOT_STARTED
            ),
            Game(
                id = "${teamId}_vs_internacional",
                competitionId = "brasileirao",
                homeTeamId = teamId,
                awayTeamId = "internacional",
                teamIds = listOf(teamId, "internacional"),
                kickoff = "2026-11-15T16:00:00Z",
                date = "2026-11-15",
                channelsByCountry = mapOf("BR" to listOf("globo", "premiere")),
                broadcastStatusByCountry = mapOf("BR" to BroadcastStatus.CONFIRMED),
                matchStatus = MatchStatus.NOT_STARTED
            )
        )

        val hasQuiz = club?.hasAleflyQuiz ?: false
        return TeamHubData(
            teamId = teamId,
            nextGame = nextGame,
            recentMatches = recentMatches,
            upcomingMatches = upcomingMatches,
            quizTitle = "Quiz do $nickname",
            quizDescription = "Teste seus conhecimentos sobre a história, títulos e ídolos do $clubName no Alefly.",
            quizUrl = club?.aleflyQuizUrl ?: "https://alefly.app/${club?.aleflyTenantId ?: teamId}",
            hasQuiz = hasQuiz
        )
    }

    /**
     * Guia de Canais com a programação esportiva por emissora.
     */
    fun getChannelsGuide(date: String): Map<String, List<Game>> {
        val games = listOf(
            Game(
                id = "flamengo_vs_palmeiras",
                competitionId = "brasileirao",
                homeTeamId = "flamengo",
                awayTeamId = "palmeiras",
                teamIds = listOf("flamengo", "palmeiras"),
                kickoff = "${date}T16:00:00Z",
                date = date,
                channelsByCountry = mapOf("BR" to listOf("globo", "premiere")),
                broadcastStatusByCountry = mapOf("BR" to BroadcastStatus.CONFIRMED),
                matchStatus = MatchStatus.FIRST_HALF,
                homeScore = 2,
                awayScore = 1,
                stadium = "Maracanã"
            ),
            Game(
                id = "saopaulo_vs_corinthians",
                competitionId = "brasileirao",
                homeTeamId = "saopaulo",
                awayTeamId = "corinthians",
                teamIds = listOf("saopaulo", "corinthians"),
                kickoff = "${date}T18:30:00Z",
                date = date,
                channelsByCountry = mapOf("BR" to listOf("premiere", "cazetv")),
                broadcastStatusByCountry = mapOf("BR" to BroadcastStatus.CONFIRMED),
                matchStatus = MatchStatus.NOT_STARTED,
                stadium = "MorumBIS"
            ),
            Game(
                id = "vasco_vs_fluminense",
                competitionId = "brasileirao",
                homeTeamId = "vasco",
                awayTeamId = "fluminense",
                teamIds = listOf("vasco", "fluminense"),
                kickoff = "${date}T20:00:00Z",
                date = date,
                channelsByCountry = mapOf("BR" to listOf("sportv", "premiere")),
                broadcastStatusByCountry = mapOf("BR" to BroadcastStatus.CONFIRMED),
                matchStatus = MatchStatus.NOT_STARTED,
                stadium = "São Januário"
            ),
            Game(
                id = "realmadrid_vs_barcelona",
                competitionId = "champions-league",
                homeTeamId = "realmadrid",
                awayTeamId = "barcelona",
                teamIds = listOf("realmadrid", "barcelona"),
                kickoff = "${date}T17:00:00Z",
                date = date,
                channelsByCountry = mapOf("BR" to listOf("tnt", "max")),
                broadcastStatusByCountry = mapOf("BR" to BroadcastStatus.CONFIRMED),
                matchStatus = MatchStatus.NOT_STARTED,
                stadium = "Santiago Bernabéu"
            ),
            Game(
                id = "arsenal_vs_chelsea",
                competitionId = "premier-league",
                homeTeamId = "arsenal",
                awayTeamId = "chelsea",
                teamIds = listOf("arsenal", "chelsea"),
                kickoff = "${date}T13:30:00Z",
                date = date,
                channelsByCountry = mapOf("BR" to listOf("espn", "disney")),
                broadcastStatusByCountry = mapOf("BR" to BroadcastStatus.CONFIRMED),
                matchStatus = MatchStatus.FINISHED,
                homeScore = 3,
                awayScore = 1,
                stadium = "Emirates Stadium"
            )
        )

        val channelMap = mutableMapOf<String, MutableList<Game>>()
        listOf("globo", "premiere", "sportv", "cazetv", "espn", "disney", "tnt", "max").forEach { ch ->
            channelMap[ch] = mutableListOf()
        }

        games.forEach { game ->
            game.channelsFor("BR").forEach { ch ->
                channelMap.getOrPut(ch) { mutableListOf() }.add(game)
            }
        }

        return channelMap
    }

    /**
     * Gera um jogo fake garantido para qualquer slug (ex: 2026-11-20/flamengo-vs-palmeiras).
     */
    fun getFallbackGameForSlug(slug: String): Game {
        val parts = slug.split("/").filter { it.isNotBlank() }
        val datePart = parts.firstOrNull { it.matches(Regex("\\d{4}-\\d{2}-\\d{2}")) } ?: "2026-11-02"
        val matchupPart = parts.lastOrNull { !it.matches(Regex("\\d{4}-\\d{2}-\\d{2}")) } ?: slug

        val teams = matchupPart
            .replace("-x-", "_vs_")
            .replace("-vs-", "_vs_")
            .replace("x", "_vs_")
            .split("_vs_")

        val homeSlug = teams.firstOrNull()?.trim()?.ifBlank { "flamengo" } ?: "flamengo"
        val awaySlug = teams.getOrNull(1)?.trim()?.ifBlank { "palmeiras" } ?: "palmeiras"

        return Game(
            id = "${homeSlug}_vs_$awaySlug",
            competitionId = "brasileirao",
            matchday = 33,
            homeTeamId = homeSlug,
            awayTeamId = awaySlug,
            teamIds = listOf(homeSlug, awaySlug),
            kickoff = "${datePart}T16:00:00Z",
            date = datePart,
            channelsByCountry = mapOf("BR" to listOf("globo", "premiere")),
            broadcastStatusByCountry = mapOf("BR" to BroadcastStatus.CONFIRMED),
            matchStatus = MatchStatus.NOT_STARTED,
            stadium = "Estádio Principal",
            stadiumCity = "Brasil"
        )
    }
}
